import { useEffect, useState } from "react";
import { Blurhash } from "react-blurhash";
import { cn } from "~/lib/utils";
import { BLUR_HASH } from "~/utils/constant";

interface ImageData {
  id: number;
  url: {
    small: string;
    medium: string;
    large: string;
    hd: string;
    original: string;
  };
  width: number;
  height: number;
  blur_hash: string;
  alt: string;
}

interface ImageWithBlurHashProps {
  data: ImageData;
  handleImageClick: (id: number) => void;
  className?: string;
}

function ImageWithBlurHash({ data, handleImageClick, className }: ImageWithBlurHashProps) {
  const fallbackBlurHash = BLUR_HASH;
  const [loading, setLoading] = useState(true); // Start with loading true

  let blurHash = data.blur_hash || fallbackBlurHash;

  const handleClick = () => {
    handleImageClick(data.id); // Pass image ID
  };

  useEffect(() => {
    const image = new Image();

    image.onload = () => {
      setLoading(false);
    };

    image.src = data.url.medium; // Load the image
  }, [data.url.medium]); // Load the image when the component mounts

  return (
    <div className={cn("mr-0 overflow-hidden rounded-xl", className)} key={data.id}>
      {loading && (
        <div className="w-full h-full object-cover rounded-lg shadow-md overflow-hidden">
          <Blurhash hash={blurHash} width={data.width} height={data.height} resolutionX={4} resolutionY={4} punch={1} />
        </div>
      )}
      <div className="cursor-zoom-in relative" onClick={handleClick}>
        <picture>
          <source media="(min-width: 1024px)" srcSet={`${data.url.medium} 1x, ${data.url.medium} 2x`} />
          <source media="(min-width: 640px)" srcSet={`${data.url.medium} 1x, ${data.url.medium} 2x`} />
          <source media="(max-width: 425px)" srcSet={`${data.url.small} 1x, ${data.url.small} 2x`} />
          <img
            src={data.url.small}
            alt={data.alt}
            className="w-full h-full object-cover rounded-lg shadow-md select-none"
            loading="lazy"
            onLoad={() => {
              setLoading(false);
            }}
            style={{
              height: data.height,
            }}
          />
        </picture>
      </div>
    </div>
  );
}

export default ImageWithBlurHash;
